import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
     
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Product Profit Report Display Component
const ProductProfitReportDisplay = ({ formData, branchProfile, salesData, productData }) => {
  const filteredSalesData = salesData.filter(item => {
    const itemDate = new Date(item.date.split('-').reverse().join('-')); // Convert DD-MM-YYYY to YYYY-MM-DD
    const fromDate = formData.fromDate ? new Date(formData.fromDate) : null;
    const toDate = formData.toDate ? new Date(formData.toDate) : null;
    let matchesFilters = true;
    if (fromDate) matchesFilters = matchesFilters && itemDate >= fromDate;
    if (toDate) matchesFilters = matchesFilters && itemDate <= toDate;
    return matchesFilters;
  });

  // Aggregate sales data by product and calculate profit using product form data
  const aggregatedData = filteredSalesData.reduce((acc, item) => {
    const key = item.itemDescription;
    const product = productData.find(p => p.productName === item.itemDescription) || {};

    if (!acc[key]) {
      const qty = parseFloat(item.qty) || 0;

      // Calculate selling price including tax (Selling Price Incl. Tax)
      const unitRate = parseFloat(item.unitRate) || 0;
      const tax = parseFloat(item.tax) || 0;
      const discPercent = parseFloat(item.discPercent) || 0;
      const sellingPriceInclTax = unitRate + (unitRate * tax / 100);
      const salesAmount = sellingPriceInclTax * qty;

      // Calculate discount amount
      const baseAmount = unitRate * qty;
      const discountAmount = (baseAmount * discPercent) / 100;

      // Get purchase rate from product form (Purchase Rate Excl Tax)
      const purchaseRateExclTax = parseFloat(product.purchaseRateExclTax) || 0;
      const purchaseAmount = purchaseRateExclTax * qty;

      acc[key] = {
        itemDescription: item.itemDescription || 'Unknown Product',
        pCode: product.pCode || `${String(Object.keys(acc).length + 1).padStart('0')}`,
       
        totalQty: qty,
        purchaseAmount: purchaseAmount,
        salesAmount: salesAmount,
        discAmt: discountAmount,
        profit: salesAmount - purchaseAmount
      };
    } else {
      const qty = parseFloat(item.qty) || 0;

      // Calculate selling price including tax (Selling Price Incl. Tax)
      const unitRate = parseFloat(item.unitRate) || 0;
      const tax = parseFloat(item.tax) || 0;
      const discPercent = parseFloat(item.discPercent) || 0;
      const sellingPriceInclTax = unitRate + (unitRate * tax / 100);
      const salesAmount = sellingPriceInclTax * qty;

      // Calculate discount amount
      const baseAmount = unitRate * qty;
      const discountAmount = (baseAmount * discPercent) / 100;

      // Get purchase rate from product form (Purchase Rate Excl Tax)
      const purchaseRateExclTax = parseFloat(product.purchaseRateExclTax) || 0;
      const purchaseAmount = purchaseRateExclTax * qty;

      acc[key].totalQty += qty;
      acc[key].salesAmount += salesAmount;
      acc[key].purchaseAmount += purchaseAmount;
      acc[key].discAmt += discountAmount;
      acc[key].profit = acc[key].salesAmount - acc[key].purchaseAmount;
    }
    return acc;
  }, {});

  const sortedData = Object.values(aggregatedData).sort((a, b) =>
    a.itemDescription.localeCompare(b.itemDescription)
  );

  const totalTotalQty = sortedData.reduce((sum, item) => sum + item.totalQty, 0);
  const totalPurchaseAmount = sortedData.reduce((sum, item) => sum + item.purchaseAmount, 0);
  const totalSalesAmount = sortedData.reduce((sum, item) => sum + item.salesAmount, 0);
  const totalDiscAmt = sortedData.reduce((sum, item) => sum + item.discAmt, 0);
  const totalProfit = sortedData.reduce((sum, item) => sum + item.profit, 0);

  const reportTitle = 'Product Profit Report';
  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {reportTitle}
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'All'}</span>
              <span><strong>To:</strong> {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'All'}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'details' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>
      {/* Profit Table */}
      <div style={{ overflowX: 'auto' }}>
      <table style={{
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '20px',
  border: '2px solid #3b82f6',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}} className="product-profit">
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>PCode</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>

              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total Qty</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Purchase Amount</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Sales Amount</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc. Amt</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Profit</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan="9" style={{
                  border: '1px solid #cbd5e1',
                  padding: '30px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: '#f8fafc'
                }}>
                  No profit data found for the selected criteria
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={`${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.pCode}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
          
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.totalQty.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.purchaseAmount.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.salesAmount.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.discAmt.toFixed(2)}</td>
                  <td style={{
                    border: '1px solid #cbd5e1',
                    padding: '8px',
                    textAlign: 'right',
                    fontSize: '11px',
                    color: item.profit >= 0 ? '#16a34a' : '#dc2626',
                    fontWeight: '500'
                  }}>
                    ₹{item.profit.toFixed(2)}
                  </td>
                </tr>
              ))
            )}

            {sortedData.length > 0 && (
              <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
                  Grand Total
                </td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalTotalQty.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalPurchaseAmount.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalSalesAmount.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
                <td style={{
                  border: '1px solid #cbd5e1',
                  padding: '10px',
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: totalProfit >= 0 ? '#16a34a' : '#dc2626'
                }}>
                  ₹{totalProfit.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Summary Section */}
      {sortedData.length > 0 && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Products</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{sortedData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#2563eb' }}>{totalTotalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Sales</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalSalesAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Profit</div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: totalProfit >= 0 ? '#16a34a' : '#dc2626'
              }}>
                ₹{totalProfit.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Product Profit Report Generator Component
const ProductProfitReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("datewise");
  const [reportType, setReportType] = useState("details");
  const [filters, setFilters] = useState({
    product: ["All Products"]
  });
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [salesData, setSalesData] = useState([]);
  const [productData, setProductData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const productList = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            productList.push({
              id: childSnapshot.key,
              productName: product.productName,
              pCode: product.pCode || `${String(productList.length + 1).padStart('0')}`,
           
              purchaseRateExclTax: parseFloat(product.purchaseRateExclTax) || 0,
              sellingRate: parseFloat(product.sellingRate) || 0,
              mrp: parseFloat(product.mrp) || 0
            });
          }
        });
      }
      setProducts(productsData);
      setProductData(productList);
    });

    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const salesRecords = [];
        Object.entries(data).forEach(([saleId, saleData]) => {
          if (saleData.items && Array.isArray(saleData.items)) {
            saleData.items.forEach(item => {
              if (item.itemDescription) {
                salesRecords.push({
                  saleId,
                  date: saleData.date || '',
                  itemDescription: item.itemDescription || 'Unknown Product',
                  qty: parseFloat(item.qty) || 0,
                  unitRate: parseFloat(item.unitRate) || 0,
                  tax: parseFloat(item.tax) || 0,
                  discPercent: parseFloat(item.discPercent) || 0,
                  netValue: parseFloat(item.netValue) || 0
                });
              }
            });
          }
        });
        setSalesData(salesRecords);
      } else {
        setSalesData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(productsRef);
      off(salesRef);
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const handlePrint = () => {
  const printContent = (
    <ProductProfitReportDisplay
      formData={{ fromDate, toDate, searchBy, reportType }}
      branchProfile={branchProfile}
      salesData={salesData}
      productData={productData}
    />
  );
  
  const htmlString = ReactDOMServer.renderToString(printContent);
  
  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Product Profit Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 8px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
            padding: 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          
          /* Product profit report columns */
          .product-profit th:nth-child(1), .product-profit td:nth-child(1) { width: 30px; } /* S.No */
          .product-profit th:nth-child(2), .product-profit td:nth-child(2) { width: 50px; } /* PCode */
          .product-profit th:nth-child(3), .product-profit td:nth-child(3) { width: 120px; } /* Product Name */
          .product-profit th:nth-child(4), .product-profit td:nth-child(4) { width: 50px; } /* Total Qty */
          .product-profit th:nth-child(5), .product-profit td:nth-child(5) { width: 60px; } /* Purchase Amount */
          .product-profit th:nth-child(6), .product-profit td:nth-child(6) { width: 60px; } /* Sales Amount */
          .product-profit th:nth-child(7), .product-profit td:nth-child(7) { width: 50px; } /* Disc Amt */
          .product-profit th:nth-child(8), .product-profit td:nth-child(8) { width: 60px; } /* Profit */
          
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Product Profit Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        color: '#64748b',
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Product Profit Report </p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
     
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Product Profit Report </h1>
      </div>
     
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="details">Detail</option>
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>
  
      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>
      {/* Product Profit Report Display */}
      {showReport && (
        <ProductProfitReportDisplay
          formData={formData}
          branchProfile={branchProfile}
          salesData={salesData}
          productData={productData}
        />
      )}
    </div>
  );
};

export default ProductProfitReportGenerator;